// Native v4 error view — props port of the v1 errorPages/Error.tsx (antd
// Result) so lockdown/take surfaces render an error without antd (R15b-b2f).
// Same knobs as v3: title/subTitle, home + refresh buttons, optional redirect,
// customExtra slot. Wire kept verbatim: refine useNavigation().push, 3s
// redirect. Deltas (MIGRATION § R15b-b2f): antd Result → shadcn, antd message →
// sonner (video-call precedent).
// Re-homed from pages/system (QA-29 review): consumed by several page worlds
// (system, experiences take + lockdownConfig) — the three-layer import law
// routes cross-page sharing through components/ap.
import { CloseCircleBold } from '@assessprep/icons/solar';
import { useNavigation } from '@refinedev/core';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { APStatusView } from '@/components/ap/status/APStatusView';
import { Button } from '@/components/ui/button';

export interface ErrorViewProps {
  title?: string;
  subTitle?: string;
  showHomeButton?: boolean;
  showRefreshButton?: boolean;
  onRefresh?: () => void;
  shouldRedirect?: boolean;
  redirectTo?: string;
  customExtra?: React.ReactNode;
  /** Layout pass-through (e.g. min-h presence inside a take viewport). */
  className?: string;
  /** Surface-specific testid (defaults to the shared v4-error-view). */
  testId?: string;
}

export const ErrorView = (props: ErrorViewProps) => {
  const { t } = useTranslation();
  const { push } = useNavigation();
  const hasShown = useRef(false);
  const {
    title = t('CommonText.an_error_occurred'),
    subTitle = t('Error.sorry_something_went_wrong'),
    shouldRedirect = false,
    showHomeButton = shouldRedirect ? false : true,
    showRefreshButton = false,
    redirectTo = '/',
    onRefresh,
    customExtra,
    className,
    testId = 'v4-error-view',
  } = props;

  // v3: 3s toast-then-redirect for the auto-redirect flavor.
  useEffect(() => {
    if (!shouldRedirect || hasShown.current) return;
    hasShown.current = true;
    toast.error(title);
    const timeoutId = setTimeout(() => push(redirectTo), 3000);
    return () => clearTimeout(timeoutId);
  }, [shouldRedirect, title, redirectTo, push]);

  const handleRefresh = () => (onRefresh ? onRefresh() : window.location.reload());

  return (
    <APStatusView
      className={className}
      tone="danger"
      icon={<CloseCircleBold />}
      title={title}
      description={subTitle}
      testId={testId}
      actions={
        <>
          {showHomeButton && (
            <Button onClick={() => push(redirectTo)} data-testid="v4-error-view-home">
              {t('Error.go_home')}
            </Button>
          )}
          {showRefreshButton && (
            <Button variant="outline" onClick={handleRefresh} data-testid="v4-error-view-refresh">
              {t('CommonText.refresh')}
            </Button>
          )}
          {customExtra}
        </>
      }
    />
  );
};

export default ErrorView;
